import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faWallet,
    faCog,
    faBars,
    faCalendarPlus,
    faHouse,
    faFileAlt,
    faEye,
    faEyeSlash,
    faCalculator,
    faComment,
    faUserCheck,
    faHandHoldingUsd,
    faUsers,
    faChartLine,
    faChevronDown,
    faChevronRight,
    faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth0 } from '../../auth/useAuth0';
import { useTranslation } from 'react-i18next';
import { useBusinessContext } from '../../context/BusinessContext';
import {
    getBusinessUnreadCount,
    teamUnreadCount,
    getBusinessConversations,
    myTeamConversations,
} from '../../service/chat/chat-service';
import './LeftPanelComponent.css';
import { MENU, menuAllowed } from "../util/Utils";
import { usePlan } from "../../context/PlanContext";
import { PlanFeatureKey } from "../../service/plan/plan-service";
import PlanCrown from "../plan/PlanCrown";
import { usePermissions } from "../../context/PermissionsContext";
import { useAlert } from "../../context/alert/AlertContext";

interface LeftPanelProps {
    setActiveComponent: (componentName: string) => void;
    selected: string;
}

/**
 * Menu items. `children` makes the item a parent — its children render
 * indented under it when the parent is active (or a child is selected).
 * Add a parent's children here, not as siblings, so the navigation tree
 * stays in one place.
 */
type MenuItem = {
    key: string;
    label: string;
    icon: typeof faCog;
    /** Permission key that gates this item; omit to always show. */
    perm?: string;
    /** Subscription feature; when the plan lacks it the item shows a crown and
     *  opens the upsell screen instead of navigating (backend gates too). */
    plan?: PlanFeatureKey;
    children?: Array<{ key: string; label: string; perm?: string; plan?: PlanFeatureKey }>;
};

const MENU_ITEMS: ReadonlyArray<MenuItem> = [
    // Renamed from "Home": this screen IS the assistant. It stays visible without
    // the plan, crowned like every other locked feature — clicking opens the
    // upsell instead of navigating (HomeComponent lands on the Calendar).
    { key: MENU.HOME, label: 'IA', icon: faWandMagicSparkles, perm: 'AI_ASSISTANT', plan: 'AI' },
    {
        key: MENU.CALENDAR,
        label: 'Calendar',
        icon: faCalendarPlus,
        perm: 'CALENDAR',
        children: [
            // Waitlist is a sub-screen of the calendar with its OWN permission.
            // It only shows when the parent (CALENDAR) is visible AND the caller
            // can view WAITLIST.
            { key: MENU.WAITLIST, label: 'Waitlist', perm: 'WAITLIST', plan: 'WAITLIST' },
        ],
    },
    { key: MENU.APPOINTMENTS, label: 'Appointments', icon: faFileAlt,        perm: 'CALENDAR' },
    { key: MENU.PAYMENTS,     label: 'Payments',     icon: faWallet,         perm: 'PAYMENTS' },
    { key: MENU.REPORTS,      label: 'Reports',      icon: faFileAlt,        perm: 'REPORTS', plan: 'REPORTS' },
    { key: MENU.CHATS,        label: 'Mensagens',    icon: faComment,        perm: 'CHAT', plan: 'CHAT' },
    { key: MENU.CLIENT,       label: 'Clients',      icon: faUsers,          perm: 'CLIENTS' },
    { key: MENU.SERVICES,     label: 'Services',     icon: faHandHoldingUsd, perm: 'SERVICES' },
    { key: MENU.TEAM_MEMBER,  label: 'Team Member',  icon: faUserCheck,      perm: 'TEAM' },
    { key: MENU.PAYROLL,  label: 'Payroll',    icon: faCalculator, perm: 'PAYROLL', plan: 'COMMISSIONS' },
    { key: MENU.FINANCE,  label: 'Financeiro', icon: faChartLine,  perm: 'EXPENSES', plan: 'FINANCE' },
    { key: MENU.SETTINGS, label: 'Settings',   icon: faCog,        perm: 'BUSINESS_SETTINGS' },
];

const LeftPanelComponent: React.FC<LeftPanelProps> = ({ setActiveComponent, selected }) => {
    const { t } = useTranslation();
    const { canView } = usePermissions();
    const { hasFeature, openUpsell } = usePlan();
    const { getAccessTokenSilently } = useAuth0();
    const { selectedBusiness } = useBusinessContext();
    const { setAlert } = useAlert();

    // Keep the current screen readable inside the polling closure (so we don't
    // toast a "new message" while the user is already on the Mensagens screen).
    const selectedRef = useRef(selected);
    selectedRef.current = selected;

    // --- Mensagens unread badge + new-message toast ---
    const [chatUnread, setChatUnread] = useState<number>(0);
    // Previous total per business — null until the first poll so we never toast
    // on initial load / business switch, only on a real increase afterwards.
    const prevUnreadRef = useRef<number | null>(null);
    useEffect(() => {
        const businessId = selectedBusiness?.id;
        prevUnreadRef.current = null; // reset baseline when the business changes
        if (!businessId) { setChatUnread(0); return; }
        let cancelled = false;
        const fetchCount = async () => {
            try {
                const token = await getAccessTokenSilently();
                const [clientCount, teamCount] = await Promise.all([
                    getBusinessUnreadCount(businessId, token),
                    teamUnreadCount(businessId, token),
                ]);
                if (cancelled) return;
                const total = clientCount + teamCount;
                setChatUnread(total);
                const prev = prevUnreadRef.current;
                prevUnreadRef.current = total;
                // A real increase while the user isn't already reading Mensagens →
                // heads-up toast. Resolve the most-recent unread sender's name
                // (best-effort; no message content is shown).
                if (prev !== null && total > prev && selectedRef.current !== MENU.CHATS) {
                    let who: string | undefined;
                    try {
                        const [biz, team] = await Promise.all([
                            getBusinessConversations(businessId, token).catch(() => []),
                            myTeamConversations(businessId, token).catch(() => []),
                        ]);
                        const cands = [
                            ...biz.filter((c) => (c.unreadForBusiness ?? 0) > 0)
                                .map((c) => ({ name: c.clientName || c.clientEmail || '', at: c.lastMessageAt || '' })),
                            ...team.filter((c) => (c.unread ?? 0) > 0)
                                .map((c) => ({ name: c.otherName || c.otherEmail || '', at: c.lastAt || '' })),
                        ].sort((a, b) => b.at.localeCompare(a.at));
                        who = cands[0]?.name || undefined;
                    } catch { /* ignore — fall back to the generic toast */ }
                    if (!cancelled) {
                        setAlert('success', who
                            ? t('messages.newMessageFrom', { name: who })
                            : t('messages.newMessage'));
                    }
                }
            } catch { /* non-critical — badge stays at its last value */ }
        };
        fetchCount();
        const intervalId = window.setInterval(fetchCount, 15000);
        return () => { cancelled = true; window.clearInterval(intervalId); };
    }, [selectedBusiness?.id, getAccessTokenSilently, setAlert, t]);

    // Immediate re-poll when a conversation is read/sent elsewhere (MessagesComponent
    // dispatches 'gs-chat-unread-refresh'), so the badge drops the instant the user
    // reads instead of sitting "stuck" until the next 15 s tick. Keeps prevUnreadRef
    // in sync so this refresh never triggers a false "new message" toast.
    useEffect(() => {
        const businessId = selectedBusiness?.id;
        if (!businessId) return;
        const onRefresh = async () => {
            try {
                const token = await getAccessTokenSilently();
                const [clientCount, teamCount] = await Promise.all([
                    getBusinessUnreadCount(businessId, token),
                    teamUnreadCount(businessId, token),
                ]);
                const total = clientCount + teamCount;
                setChatUnread(total);
                prevUnreadRef.current = total;
            } catch { /* non-critical */ }
        };
        window.addEventListener('gs-chat-unread-refresh', onRefresh);
        return () => window.removeEventListener('gs-chat-unread-refresh', onRefresh);
    }, [selectedBusiness?.id, getAccessTokenSilently]);
    // Hide nav items the caller can't access (backend still enforces every op).
    // Uses the shared MENU_PERMISSION map (Settings = any settings permission).
    const visibleItems = MENU_ITEMS.filter((item) => menuAllowed(item.key, canView));
    const [selectedComponent, setSelectedComponent] = useState<string>(selected);
    // Follow external changes to the active screen (restore on F5, permission
    // redirect) so the highlighted item matches the content.
    useEffect(() => {
        setSelectedComponent(selected);
    }, [selected]);
    const [isCollapsed, setIsCollapsed] = useState(false);
    /** Parents the user has explicitly expanded via the chevron. Starts empty —
     *  we do NOT auto-seed, otherwise a submenu would stay open (and add a row,
     *  causing the sidebar to scroll) even after navigating to an unrelated
     *  screen. A submenu still auto-shows when one of its children is the
     *  active screen (see `expanded` below). */
    const [openParents, setOpenParents] = useState<Set<string>>(new Set());

    const handleComponentChange = (componentName: string, planFeature?: PlanFeatureKey) => {
        if (planFeature && !hasFeature(planFeature)) {
            openUpsell(planFeature);
            return;
        }
        setActiveComponent(componentName);
        setSelectedComponent(componentName);
        // No need to force the parent open here — `expanded` already auto-shows
        // a submenu whose child is the active screen, and collapses it once you
        // navigate away (so it never leaves a stray row that triggers a scroll).
    };

    const toggleParent = (key: string) => {
        setOpenParents((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    /** True only when a CHILD of this parent is the active screen — used to
     *  auto-expand the submenu so the user sees where they are. Selecting the
     *  parent itself (e.g. Calendar) does NOT force the submenu open. */
    const isChildActive = (item: MenuItem) =>
        !!item.children?.some((c) => c.key === selectedComponent);

    const toggleMenu = () => {
        setIsCollapsed(!isCollapsed);
    };

    return (
        <nav className={`sidebar ${isCollapsed ? 'collapsed' : ''}`} data-cy="nav-panel">
            {/* Logo e botão de colapsar */}
            <div className="sidebar-header">
                <div className="logo-container">
                    {/*<FontAwesomeIcon icon={faHome} className="logo-icon" />*/}
                    {!isCollapsed && <span className="logo-text">{t('nav.business')}</span>}
                </div>
                <button className="collapse-btn" onClick={toggleMenu}>
                    <FontAwesomeIcon icon={faBars} />
                </button>
            </div>

            {/* Items ordered by daily-use frequency:
                 calendar > appointments > payments > reports > messages >
                 clients > services > team > payroll > finance > settings. */}
            <div className="menu-items">
                <ul className="nav flex-column">
                    {visibleItems.map((item) => {
                        // Children carry their own permission (e.g. Waitlist =
                        // WAITLIST); a child only renders when its perm is viewable.
                        const visibleChildren = (item.children ?? []).filter(
                            (c) => !c.perm || canView(c.perm)
                        );
                        const hasChildren = visibleChildren.length > 0;
                        const childActive = isChildActive(item);
                        // Expanded when the user toggled it open OR a child is the
                        // active screen. Collapses again once you navigate away, so
                        // it never leaves a stray row that forces the sidebar to scroll.
                        const expanded = openParents.has(item.key) || childActive;
                        // Highlight the parent row softly while inside one of its children.
                        const parentActive = childActive;
                        return (
                            <li className="nav-item" key={item.key}>
                                <a
                                    className={`nav-link ${selectedComponent === item.key ? 'selected' : ''} ${
                                        hasChildren ? 'has-children' : ''
                                    } ${parentActive && hasChildren ? 'parent-active' : ''}`}
                                    onClick={() => handleComponentChange(item.key, item.plan)}
                                    data-cy={`nav-${item.key}`}
                                >
                                    <FontAwesomeIcon icon={item.icon} fixedWidth className="nav-link__icon" />
                                    {!isCollapsed && <span className="nav-link__label">{t(`nav.${item.key}`, item.label)}</span>}
                                    {!isCollapsed && item.plan && !hasFeature(item.plan) && <PlanCrown />}
                                    {/* Unread badge — only on Mensagens, only when count > 0 */}
                                    {item.key === MENU.CHATS && chatUnread > 0 && (
                                        <span className="nav-unread-badge">
                                            {chatUnread > 9 ? '9+' : chatUnread}
                                        </span>
                                    )}
                                    {hasChildren && !isCollapsed && (
                                        <button
                                            type="button"
                                            className="nav-link__chev"
                                            onClick={(e) => { e.stopPropagation(); toggleParent(item.key); }}
                                            aria-label={expanded ? t('nav.collapse') : t('nav.expand')}
                                        >
                                            <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
                                        </button>
                                    )}
                                </a>
                                {hasChildren && expanded && !isCollapsed && (
                                    <ul className="nav flex-column nav-submenu">
                                        {visibleChildren.map((child) => (
                                            <li className="nav-item nav-item--child" key={child.key}>
                                                <a
                                                    className={`nav-link nav-link--child ${
                                                        selectedComponent === child.key ? 'selected' : ''
                                                    }`}
                                                    onClick={() => handleComponentChange(child.key, child.plan)}
                                                    data-cy={`nav-${child.key}`}
                                                >
                                                    <span className="nav-link__bullet" />
                                                    {t(`nav.${child.key}`, child.label)}
                                                    {child.plan && !hasFeature(child.plan) && <PlanCrown />}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
        </nav>
    );
};

export default LeftPanelComponent;